import React, { useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import ScrollMagic from 'scrollmagic';
import {
  ColumnsWrapper,
  ColumnsInnerWrapper,
  SectionWrapper,
  StyledBox,
  StyledCloudImage,
  StyledTextPairing,
  StyledBlogTextPairing,
  AnimationWrapper,
} from './MessagingInvitationsSection.styles';
import { Columns } from '@components/Columns';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { useTranslation } from 'react-i18next';

export const MessagingInvitationsSection: React.FC = () => {
  const { t } = useTranslation('guestList');
  const messagingInvitationsData = t('messagingInvitations');
  const TextPairings = messagingInvitationsData.columns.map((paragraph, index) => (
    <StyledBlogTextPairing
      title={paragraph.title}
      titleColor="white"
      titleVariant="sectionEyeBrow24"
      titleTagName="h3"
      body={paragraph.content}
      bodyColor="neutra1"
      bodyVariant="dek20"
      key={index}
    />
  ));
  const messagingInvitationsSectionId = 'All_the_Cards';
  const invitationsCardsId = 'Invitations_Cards';
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const isMobileDevice = useThemeMediaQuery('below_sm');
  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let timeline: ReturnType<typeof gsap.timeline>;
    if (scrollController && isMounted) {
      timeline = gsap.timeline({ paused: true });
      timeline.to(`#${invitationsCardsId}`, { y: isMobileDevice ? '50%' : '25%' }, 0);
      scene = new ScrollMagic.Scene({
        triggerElement: `#${messagingInvitationsSectionId}`,
        triggerHook: 1,
        duration: '100%',
        offset: 800,
      })
        .addTo(scrollController)
        .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
          timeline?.progress(event.progress);
        });
    }
    const clearScene = () => {
      if (timeline) {
        timeline.progress(0);
        timeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };

    return clearScene;
  }, [scrollController, isMobileDevice, isMounted]);

  return (
    <SectionWrapper backgroundColor="green5" paddingTop={0} paddingBottom={[4, 12]} id={messagingInvitationsSectionId}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)}>
        <div />
      </LazyLoadWrapper>
      <StyledBox paddingTop={[9, 13]} paddingBottom={[11, 15]} isVisible={isVisible}>
        <Columns verticalAlign="top">
          <Columns.Column width="full">
            <AnimationWrapper id={invitationsCardsId}>
              <StyledCloudImage alt={messagingInvitationsData.imgAlt} src="guest-list/messaging-invitations/cards.png" />
            </AnimationWrapper>
          </Columns.Column>
        </Columns>
      </StyledBox>
      <StyledTextPairing
        alignment="center"
        eyebrow={messagingInvitationsData.eyebrow}
        eyebrowColor="humana3"
        eyebrowVariant="sectionEyeBrow24"
        eyebrowTagName="h2"
        title={messagingInvitationsData.title}
        titleColor="neutra1"
        titleVariant="hed56"
        titleTagName="h2"
        marginTop={[9, -6, -11]}
        paddingTop={[0, 10]}
      />
      <ColumnsWrapper verticalAlign="top" marginTop={10}>
        <ColumnsInnerWrapper width={4}>{TextPairings[0]}</ColumnsInnerWrapper>
        <ColumnsInnerWrapper width={4}> {TextPairings[1]} </ColumnsInnerWrapper>
        <ColumnsInnerWrapper width={4}>{TextPairings[2]}</ColumnsInnerWrapper>
      </ColumnsWrapper>
    </SectionWrapper>
  );
};
